/**
 * One currency pane of the Revenue Over Time chart (CHO-752): per property a
 * solid revenue line plus a dashed cost line in the same hue. Prop-driven, no
 * fetching — rows come from lib/dashboard-costs.ts derivations.
 */
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { CombinedChartRow } from '../../lib/dashboard-costs';
import { formatAxisTick, formatCurrency } from '../../lib/format';

export interface RevenueCostChartProps {
  currency: string;
  rows: CombinedChartRow[];
  /** Property line names present in this currency pane, in display order. */
  properties: string[];
  /** Line color per property name. */
  colors: Record<string, string>;
  height?: number;
}

export function RevenueCostChart({
  currency,
  rows,
  properties,
  colors,
  height = 280,
}: RevenueCostChartProps) {
  if (properties.length === 0) {
    return <p className="text-sm text-gray-500">No {currency} properties in range.</p>;
  }

  return (
    <div style={{ width: '100%', height }}>
      <ResponsiveContainer>
        <LineChart data={rows} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="month" tick={{ fontSize: 11 }} />
          <YAxis
            tick={{ fontSize: 11 }}
            tickFormatter={(v: number) => formatAxisTick(v, currency)}
            width={80}
          />
          <Tooltip
            formatter={(value, name) => [
              formatCurrency(Number(value), currency),
              name,
            ]}
          />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          {properties.map((name) => (
            <Line
              key={name}
              type="monotone"
              dataKey={name}
              stroke={colors[name] ?? '#94a3b8'}
              strokeWidth={2}
              dot={{ r: 3 }}
              connectNulls
              activeDot={{ r: 5 }}
            />
          ))}
          {properties.map((name) => (
            <Line
              key={`${name} costs`}
              type="monotone"
              dataKey={`${name} costs`}
              stroke={colors[name] ?? '#94a3b8'}
              strokeWidth={2}
              strokeDasharray="6 4"
              dot={false}
              activeDot={{ r: 4 }}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
